/**
 * LR Direct Bulk Upload CSV schema — template generation, parsing & validation.
 *
 * LR Direct-native clone of the LaneRunner bulk-upload schema. Writes target
 * `direct_loads` ONLY. The LaneRunner lib (src/lib/bulkUploadSchema.ts) is never
 * mutated — we only reuse its pure `geocodeCity` helper.
 */

import { EQUIPMENT_FLAT_LIST } from "@/lib/equipmentTypes";
import { generateLoadNumber } from "@/lib/generateLoadNumber";
import { getLocalTodayYMD } from "@/lib/dateOnly";
import { geocodeCity } from "@/lib/bulkUploadSchema";

export { geocodeCity };

/* ------------------------------------------------------------------ */
/*  Column definitions                                                 */
/* ------------------------------------------------------------------ */

export const LR_DIRECT_BULK_HEADERS = [
  "pickup_date",
  "origin_city",
  "origin_state",
  "destination_city",
  "destination_state",
  "equipment_type",
  "target_rate_min",
  "target_rate_max",
  "weight_lbs",
  "length_ft",
  "load_type",
  "load_nature",
  "commodity",
  "notes",
  "reference_id",
] as const;

/** LR Direct requires far less than LaneRunner — direct_loads defaults the rest. */
const REQUIRED_FIELDS = [
  "pickup_date",
  "origin_city",
  "origin_state",
  "destination_city",
  "destination_state",
] as const;

export const MAX_BULK_ROWS = 100;
export const SOFT_WARN_ROWS = 50;

/* ------------------------------------------------------------------ */
/*  Normalisation                                                      */
/* ------------------------------------------------------------------ */

const equipLookup: Record<string, string> = {};
for (const item of EQUIPMENT_FLAT_LIST) {
  equipLookup[item.name.toLowerCase()] = item.enumValue;
  equipLookup[item.code.toLowerCase()] = item.enumValue;
  equipLookup[item.enumValue.toLowerCase()] = item.enumValue;
}

function normalizeEquipment(raw: string): string | null {
  return equipLookup[raw.trim().toLowerCase()] ?? null;
}

const LOAD_TYPE_ALIASES: Record<string, string> = {
  ftl: "FTL",
  full: "FTL",
  "full load": "FTL",
  "full truckload": "FTL",
  ltl: "LTL",
  partial: "LTL",
  other: "other",
};

/** Load nature (spot / dedicated / contract) — separate from equipment size. */
const LOAD_NATURE_ALIASES: Record<string, string> = {
  spot: "spot",
  "spot market": "spot",
  dedicated: "dedicated",
  contract: "contract",
};

export type DirectPostType = "load" | "quote";
export type DirectSendTo = "private" | "open" | "both";

/** direct_loads.visibility_mode enum value for a resolved routing choice. */
export function visibilityForSendTo(sendTo: DirectSendTo): "private_only" | "open_market" | "both" {
  if (sendTo === "private") return "private_only";
  if (sendTo === "both") return "both";
  return "open_market";
}

/* ------------------------------------------------------------------ */
/*  Template generation                                                */
/* ------------------------------------------------------------------ */

const HINT_ROW = [
  "# YYYY-MM-DD (required)",
  "required",
  "2-letter (required)",
  "required",
  "2-letter (required)",
  "Van/Reefer/Flatbed (default Van)",
  "(optional $)",
  "(optional $)",
  "(optional)",
  "(optional)",
  "FTL/LTL/other (size)",
  "spot/dedicated/contract",
  "(optional)",
  "(optional)",
  "(optional)",
];

const EXAMPLE_ROW = [
  getLocalTodayYMD(),
  "Dallas",
  "TX",
  "Chicago",
  "IL",
  "Van",
  "2600",
  "2900",
  "42000",
  "53",
  "FTL",
  "spot",
  "Electronics",
  "Dock closes 4pm",
  "REF123",
];

export function generateLRDirectTemplateCSV(): string {
  return `${LR_DIRECT_BULK_HEADERS.join(",")}\n${HINT_ROW.join(",")}\n${EXAMPLE_ROW.join(",")}\n`;
}

export function downloadLRDirectTemplateCSV() {
  const blob = new Blob([generateLRDirectTemplateCSV()], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "LR_Direct_Bulk_Load_Template.csv";
  a.click();
  URL.revokeObjectURL(url);
}

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface DirectBulkRow {
  rowNumber: number;
  data: Record<string, string>;
  normalised?: {
    equipment_type: string;
    load_type: string;
    load_nature: string;
    target_rate_min: number | null;
    target_rate_max: number | null;
    weight_lbs: number | null;
    length_ft: number | null;
  };
}

export interface DirectBulkRowError {
  row: DirectBulkRow;
  errors: string[];
}

export interface DirectBulkParseResult {
  validRows: DirectBulkRow[];
  invalidRows: DirectBulkRowError[];
  totalRows: number;
}

/* ------------------------------------------------------------------ */
/*  Parsing                                                            */
/* ------------------------------------------------------------------ */

function parseLine(line: string): string[] {
  const out: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') inQuotes = !inQuotes;
    else if (ch === "," && !inQuotes) {
      out.push(current.trim());
      current = "";
    } else current += ch;
  }
  out.push(current.trim());
  return out;
}

/* ------------------------------------------------------------------ */
/*  Validation                                                         */
/* ------------------------------------------------------------------ */

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function optionalPositive(raw: string | undefined, field: string, errs: string[]): number | null {
  const v = (raw ?? "").trim();
  if (v === "") return null;
  const num = Number(v.replace(/[$,]/g, ""));
  if (isNaN(num) || num <= 0) {
    errs.push(`${field} must be a positive number (got "${v}")`);
    return null;
  }
  return num;
}

export function validateDirectRow(row: DirectBulkRow): string[] {
  const errs: string[] = [];
  const d = row.data;

  for (const field of REQUIRED_FIELDS) {
    if (!d[field] || d[field].trim() === "") errs.push(`Missing required field: ${field}`);
  }
  if (errs.length > 0) return errs;

  // Date — format + not in the past (LR Direct posts are live auctions)
  if (!DATE_RE.test(d.pickup_date.trim())) {
    errs.push(`pickup_date must be YYYY-MM-DD (got "${d.pickup_date}")`);
  } else if (d.pickup_date.trim() < getLocalTodayYMD()) {
    errs.push(`pickup_date ${d.pickup_date.trim()} is in the past`);
  }

  // State codes
  for (const field of ["origin_state", "destination_state"] as const) {
    if (!/^[A-Za-z]{2}$/.test(d[field].trim())) {
      errs.push(`${field} must be a 2-letter state code (got "${d[field]}")`);
    }
  }

  // Equipment (optional → van)
  const rawEquip = (d.equipment_type ?? "").trim();
  let equipment = "van";
  if (rawEquip !== "") {
    const norm = normalizeEquipment(rawEquip);
    if (!norm) errs.push(`Unrecognised equipment "${rawEquip}"`);
    else equipment = norm;
  }

  // load_type = equipment SIZE (optional → FTL)
  const rawLoadType = (d.load_type ?? "").trim().toLowerCase();
  let loadType = "FTL";
  if (rawLoadType !== "") {
    // Legacy CSVs may still carry "contract" in the size column — treat as FTL + contract nature.
    if (rawLoadType === "contract") {
      loadType = "FTL";
      d.load_nature = d.load_nature?.trim() ? d.load_nature : "contract";
    } else {
      const norm = LOAD_TYPE_ALIASES[rawLoadType];
      if (!norm) errs.push(`Invalid load_type "${d.load_type}" — use FTL, LTL or other`);
      else loadType = norm;
    }
  }

  // load_nature = load NATURE (optional → spot)
  const rawLoadNature = (d.load_nature ?? "").trim().toLowerCase();
  let loadNature = "spot";
  if (rawLoadNature !== "") {
    const norm = LOAD_NATURE_ALIASES[rawLoadNature];
    if (!norm) errs.push(`Invalid load_nature "${d.load_nature}" — use spot, dedicated or contract`);
    else loadNature = norm;
  }

  // Optional numerics
  const rateMin = optionalPositive(d.target_rate_min, "target_rate_min", errs);
  const rateMax = optionalPositive(d.target_rate_max, "target_rate_max", errs);
  const weight = optionalPositive(d.weight_lbs, "weight_lbs", errs);
  const length = optionalPositive(d.length_ft, "length_ft", errs);
  if (rateMin != null && rateMax != null && rateMin > rateMax) {
    errs.push("target_rate_min cannot be greater than target_rate_max");
  }

  if (errs.length === 0) {
    row.normalised = {
      equipment_type: equipment,
      load_type: loadType,
      load_nature: loadNature,
      target_rate_min: rateMin,
      target_rate_max: rateMax,
      weight_lbs: weight,
      length_ft: length,
    };
  }

  return errs;
}

export function parseAndValidateDirectCSV(text: string): DirectBulkParseResult {
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== "");
  if (lines.length === 0) return { validRows: [], invalidRows: [], totalRows: 0 };

  const headers = parseLine(lines[0]).map((h) => h.toLowerCase().replace(/\s+/g, "_"));

  const validRows: DirectBulkRow[] = [];
  const invalidRows: DirectBulkRowError[] = [];

  for (let i = 1; i < lines.length; i++) {
    if (lines[i].trimStart().startsWith("#")) continue;
    const values = parseLine(lines[i]);
    const data: Record<string, string> = {};
    headers.forEach((h, idx) => {
      data[h] = values[idx] ?? "";
    });

    const row: DirectBulkRow = { rowNumber: i + 1, data };
    const errors = validateDirectRow(row);
    if (errors.length > 0) invalidRows.push({ row, errors });
    else validRows.push(row);
  }

  return { validRows, invalidRows, totalRows: validRows.length + invalidRows.length };
}

/* ------------------------------------------------------------------ */
/*  Payload builder — direct_loads ONLY                                */
/* ------------------------------------------------------------------ */

export interface GeoPoint {
  city: string;
  state: string;
  lat: number;
  lng: number;
}

/** Today 17:00 local, but never sooner than now + 6h (mirrors the DB trigger). */
export function deriveBidDeadline(pickupYMD: string): string {
  const [y, m, d] = pickupYMD.split("-").map(Number);
  const atFive = new Date(y, m - 1, d, 17, 0, 0, 0).getTime();
  const floor = Date.now() + 6 * 60 * 60 * 1000;
  return new Date(Math.max(atFive, floor)).toISOString();
}

export function buildDirectLoadPayload(args: {
  row: DirectBulkRow;
  origin: GeoPoint;
  destination: GeoPoint;
  userId: string;
  companyId: string | null;
  sendTo: DirectSendTo;
  /** Batch-level post type chosen in the upload card (no longer per-row). */
  postType: DirectPostType;
  privateNetworkId: string | null;
  maxBidders: number;
  totalMiles: number | null;
  isDemo: boolean;
}): Record<string, any> {
  const { row, origin, destination, userId, companyId, sendTo, postType, privateNetworkId, maxBidders, totalMiles, isDemo } = args;
  const d = row.data;
  const n = row.normalised!;
  const pickupDate = d.pickup_date.trim();
  const visibility = visibilityForSendTo(sendTo);
  const isPrivate = visibility === "private_only" || visibility === "both";

  return {
    load_number: generateLoadNumber(),
    created_by: userId,
    company_id: companyId,
    status: "posted",
    is_demo: isDemo,
    origin_name: origin.city,
    origin_address: `${origin.city}, ${origin.state}`,
    origin_city: origin.city,
    origin_state: origin.state,
    origin_country: "US",
    origin_postal_code: null,
    origin_latitude: origin.lat,
    origin_longitude: origin.lng,
    destination_name: destination.city,
    destination_address: `${destination.city}, ${destination.state}`,
    destination_city: destination.city,
    destination_state: destination.state,
    destination_country: "US",
    destination_postal_code: null,
    destination_latitude: destination.lat,
    destination_longitude: destination.lng,
    pickup_date: pickupDate,
    delivery_date: null,
    equipment_type: n.equipment_type,
    weight_lbs: n.weight_lbs,
    length_ft: n.length_ft,
    load_type: n.load_type,
    load_nature: n.load_nature,
    reference_number: d.reference_id?.trim() || null,
    commodity: d.commodity?.trim() || null,
    special_instructions: d.notes?.trim() || null,
    total_miles: totalMiles,
    target_rate_min: n.target_rate_min,
    target_rate_max: n.target_rate_max,
    max_bidders: maxBidders,
    bid_deadline: deriveBidDeadline(pickupDate),
    priority_tag: "standard",
    shipper_notes_to_brokers: d.notes?.trim() || null,
    posted_at: new Date().toISOString(),
    appointment_type: "fcfs",
    handling_requirements: [],
    visibility_mode: visibility,
    post_type: postType,
    private_network_id: isPrivate ? privateNetworkId : null,
  };
}

/** Pickup + delivery stop rows for a freshly inserted direct load. */
export function buildDirectStopRows(loadId: string, row: DirectBulkRow, origin: GeoPoint, destination: GeoPoint) {
  const pickupDate = row.data.pickup_date.trim();
  return [
    {
      direct_load_id: loadId,
      stop_number: 1,
      stop_type: "pickup",
      name: origin.city,
      address: `${origin.city}, ${origin.state}`,
      city: origin.city,
      state: origin.state,
      postal_code: null,
      country: "US",
      latitude: origin.lat,
      longitude: origin.lng,
      scheduled_date: pickupDate,
    },
    {
      direct_load_id: loadId,
      stop_number: 2,
      stop_type: "delivery",
      name: destination.city,
      address: `${destination.city}, ${destination.state}`,
      city: destination.city,
      state: destination.state,
      postal_code: null,
      country: "US",
      latitude: destination.lat,
      longitude: destination.lng,
      scheduled_date: pickupDate,
    },
  ];
}
